import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutContext } from "../../context/LayoutContext";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseSelect from "../../components/ui/BaseSelect";
import DateRangeShortcutPicker from "../../components/ui/DateRangeShortcutPicker";

import ExpenseKpiCards from "./ExpenseKpiCards";
import ExpenseYearTrendChart from "./ExpenseYearTrendChart";
import ClientDonutChart from "./ClientDonutChart";
import TeamBreakdownChart from "./TeamBreakdownChart";
import ProfitByCustomerChart from "./ProfitByCustomerChart";
import ProfitByTeamChart from "./ProfitByTeamChart";
import ProfitSummaryCards from "./ProfitSummaryCards";
import ProfitTrendChart from "./ProfitTrendChart";
import { fetchInvoiceAnalytics } from "../../services/api/invoiceAnalytics";
import { fetchFilterOptions } from "../../services/api/analytics";

const ALL_VALUE = "";

const toDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const now = new Date();
// Default the dashboard to the current calendar year (Jan 1 → Dec 31).
const defaultStart = toDateStr(new Date(now.getFullYear(), 0, 1));
const defaultEnd = toDateStr(new Date(now.getFullYear(), 11, 31));

const SectionHeader = ({ icon, accent, title, subtitle }) => (
  <div className="flex items-center gap-2.5">
    <span
      className="w-8 h-8 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: `${accent}15` }}
    >
      <i className={`fa-light ${icon} text-sm`} style={{ color: accent }} />
    </span>
    <div>
      <h3 className="text-[15px] font-semibold text-gray-800 leading-tight">{title}</h3>
      <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const InvoiceAnalytics = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [clientId, setClientId] = useState(ALL_VALUE);

  const { data: filterOptionsResult } = useQuery({
    queryKey: ["analytics-filterOptions"],
    queryFn: fetchFilterOptions,
    staleTime: 300_000,
  });

  const filterOptions = filterOptionsResult?.data;

  const clientOptions = [
    { value: ALL_VALUE, label: "All Clients" },
    ...(filterOptions?.clients?.map((c) => ({ value: String(c.id), label: c.clientName })) ?? []),
  ];

  const baseParams = {
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
    ...(clientId ? { clientId } : {}),
  };

  const { data: expenseResult, isLoading: expenseLoading } = useQuery({
    queryKey: ["invoice-analytics", "FREELANCER", startDate, endDate, clientId],
    queryFn: () => fetchInvoiceAnalytics({ role: "FREELANCER", ...baseParams }),
    staleTime: 60_000,
  });

  const { data: revenueResult, isLoading: revenueLoading } = useQuery({
    queryKey: ["invoice-analytics", "CLIENT", startDate, endDate, clientId],
    queryFn: () => fetchInvoiceAnalytics({ role: "CLIENT", ...baseParams }),
    staleTime: 60_000,
  });

  const expense = expenseResult?.data;
  const revenue = revenueResult?.data;

  const durationLabel =
    startDate && endDate
      ? `${startDate} – ${endDate}`
      : startDate
        ? `From ${startDate}`
        : endDate
          ? `Until ${endDate}`
          : "All time";

  const activeClientLabel = clientId
    ? clientOptions.find((o) => o.value === clientId)?.label
    : null;

  const fxMeta = expense?.fxMeta ?? revenue?.fxMeta;

  return (
    <div
      className={`bg-[#F6F7FB] sm:fixed sticky 2xl:w-[95.2%] xl:w-[93.2%] lg:w-[90.6%] md:w-[87.5%] sm:w-[95%] w-full overflow-y-auto transition-all ${
        isBannerVisible
          ? "top-[106px] h-[calc(100vh-106px)]"
          : "top-[56px] h-[calc(100vh-56px)]"
      }`}
    >
      {/* ── Header (matches Work Force Dashboard) ──────────────────────────── */}
      <div className="border-b border-zinc-200 bg-white flex-shrink-0">
        <BaseWrapper>
          <div className="flex items-center justify-between py-[7px] gap-3 flex-wrap">
            <h3 className="font-light">Finance Dashboard</h3>

            <div className="flex items-center gap-3 flex-wrap">
              {/* ── Client filter ── */}
              <div className="flex items-center gap-2">
                <BaseSelect
                  options={clientOptions}
                  className="w-[170px] p-1"
                  required
                  labelShrink={false}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="All Clients"
                />
                {clientId && (
                  <button
                    onClick={() => setClientId(ALL_VALUE)}
                    className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors whitespace-nowrap"
                    title="Clear client filter"
                  >
                    <i className="fa-solid fa-xmark text-[9px]" />
                    Clear
                  </button>
                )}
              </div>

              <DateRangeShortcutPicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
              />
            </div>
          </div>
        </BaseWrapper>
      </div>

      {/* ── Active filter badge ──────────────────────────────────────────── */}
      {activeClientLabel && (
        <div className="bg-blue-50 border-b border-blue-100 px-4 py-1.5">
          <BaseWrapper>
            <p className="text-[11px] text-blue-600 font-medium">
              <i className="fa-solid fa-filter mr-1.5" />
              Filtered by: <span className="font-semibold">{activeClientLabel}</span>
            </p>
          </BaseWrapper>
        </div>
      )}

      <BaseWrapper className="py-5 flex flex-col gap-8">
        {/* ══ Expenses ════════════════════════════════════════════════════ */}
        <section className="flex flex-col gap-4">
          <SectionHeader
            icon="fa-coins"
            accent="#4F46E5"
            title="Expenses"
            subtitle={`Engineer invoices · all amounts in USD · ${durationLabel}`}
          />
          {expenseLoading ? (
            <Spinner />
          ) : (
            <>
              <ExpenseKpiCards kpis={expense?.kpis} mode="expense" />
              <ExpenseYearTrendChart clientId={clientId} role="FREELANCER" mode="expense" />
            </>
          )}
        </section>

        <div className="border-t border-zinc-200" />

        {/* ══ Revenue ═════════════════════════════════════════════════════ */}
        <section className="flex flex-col gap-4">
          <SectionHeader
            icon="fa-money-bill-trend-up"
            accent="#16A34A"
            title="Revenue"
            subtitle={`Client invoices · all amounts in USD · ${durationLabel}`}
          />
          {revenueLoading ? (
            <Spinner />
          ) : (
            <>
              <ExpenseKpiCards kpis={revenue?.kpis} mode="revenue" />
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="xl:col-span-2">
                  <ExpenseYearTrendChart clientId={clientId} role="CLIENT" mode="revenue" />
                </div>
                <ClientDonutChart breakdown={revenue?.clientBreakdown} mode="revenue" />
              </div>
              <TeamBreakdownChart breakdown={revenue?.teamBreakdown} mode="revenue" />
            </>
          )}
        </section>

        <div className="border-t border-zinc-200" />

        {/* ══ Profit ══════════════════════════════════════════════════════ */}
        <section className="flex flex-col gap-4">
          <SectionHeader
            icon="fa-scale-balanced"
            accent="#0D9488"
            title="Profit"
            subtitle={`Revenue − Expenses · all amounts in USD · ${durationLabel}`}
          />
          {expenseLoading || revenueLoading ? (
            <Spinner />
          ) : (
            <>
              <ProfitSummaryCards
                revenue={revenue?.kpis?.totalExpense?.amountUsd ?? 0}
                expense={expense?.kpis?.totalExpense?.amountUsd ?? 0}
              />
              <ProfitTrendChart clientId={clientId} />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <ProfitByCustomerChart
                  revenueBreakdown={revenue?.clientBreakdown}
                  expenseBreakdown={expense?.clientBreakdown}
                />
                <ProfitByTeamChart
                  revenueBreakdown={revenue?.teamBreakdown}
                  expenseBreakdown={expense?.teamBreakdown}
                />
              </div>
            </>
          )}
        </section>

        {fxMeta?.source && (
          <p className="text-[10px] text-gray-400">
            <i className="fa-solid fa-circle-info mr-1" />
            Amounts converted to USD · rates from{" "}
            <span className="font-medium">{fxMeta.source}</span>
            {fxMeta.updatedAt && <>, updated {fxMeta.updatedAt.slice(0, 16)}</>}
          </p>
        )}

        <div className="h-2" />
      </BaseWrapper>
    </div>
  );
};

export default InvoiceAnalytics;
